/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

const SESSION_KEY = 'country-recommend-seen';

class CountryRecommendModal extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  #dialog = null;

  /** @type {Array<Record<string, string>>} */
  #locals = [];

  #isClosing = false;

  connectedCallback() {
    this.#dialog = /** @type {HTMLDialogElement | null} */ (this.querySelector('dialog'));
    this.#locals = this.#parseLocals();

    if (!this.#dialog || !this.#locals.length || this.#hasSeenThisSession()) return;

    this.#init();
  }

  disconnectedCallback() {
    this.#dialog?.removeEventListener('click', this.#handleDialogClick);
    this.#dialog?.removeEventListener('cancel', this.#handleCancel);
  }

  async #init() {
    const data = await this.#fetchSuggestions();
    if (!data) return;

    const local = this.#resolveSuggestion(data);
    if (!local) return;

    this.#renderMessage(local);
    this.#bindActions(local);

    this.#dialog?.addEventListener('click', this.#handleDialogClick);
    this.#dialog?.addEventListener('cancel', this.#handleCancel);

    setTimeout(() => this.#open(), 1000);
  }

  async #fetchSuggestions() {
    try {
      const shopify = /** @type {any} */ (window.Shopify);
      const root = shopify?.routes?.root ?? '/';
      const [currentLanguage = '', currentCountry = ''] = (this.dataset.currentLocale ?? '').split('-');
      const params = new URLSearchParams({
        'country[enabled]': 'true',
        'country[exclude]': currentCountry,
        'language[enabled]': 'true',
        'language[exclude]': currentLanguage,
      });
      const res = await fetch(`${root}browsing_context_suggestions.json?${params}`);
      if (!res.ok) return null;

      return await res.json();
    } catch {
      return null;
    }
  }

  /**
   * Suggestion resolution. Short-circuits to null when the visitor is already in
   * their IP-detected country (no same-country language nudge). Otherwise, 3 levels:
   * 1. Match by language + country from the `suggestions` array (most precise)
   * 2. Match by detected country + browser language
   * 3. Match by detected country + 'en' (most permissive fallback)
   *
   * @param {{ suggestions?: Array<Record<string, any>>, detected_values?: Record<string, any> }} data
   * @returns {Record<string, string> | null}
   */
  #resolveSuggestion(data) {
    const suggestions = data.suggestions ?? [];
    const detectedValues = data.detected_values ?? {};
    const detectedCountry = detectedValues.country?.handle?.toUpperCase() ?? null;
    const [, currentCountry = ''] = (this.dataset.currentLocale ?? '').split('-');

    // Already in the IP-detected country: don't recommend switching, not even to a
    // different language within that same country (e.g. SE-sv visitor offered SE-en).
    if (detectedCountry && detectedCountry === currentCountry.toUpperCase()) {
      return null;
    }

    for (const item of suggestions) {
      const language = item.parts?.language?.handle?.toLowerCase() ?? null;
      const country = item.parts?.country?.handle?.toUpperCase() ?? null;

      if (!language || !country) continue;

      const local = this.#matchLocal(country, language);
      if (local) return local;
    }

    if (!detectedCountry) return null;

    const browserLanguage = ((navigator?.language ?? 'en').split('-')[0] || 'en').toLowerCase();
    const level2 = this.#matchLocal(detectedCountry, browserLanguage);
    if (level2) return level2;

    return this.#matchLocal(detectedCountry, 'en');
  }

  /**
   * @param {string} country
   * @param {string} language
   * @returns {Record<string, string> | null}
   */
  #matchLocal(country, language) {
    return (
      this.#locals.find((local) => {
        if ((local.country_code ?? '').toUpperCase() !== country) return false;
        if ((local.language_code ?? '').toLowerCase() !== language) return false;

        const domain = local.domain ?? '';
        if (domain && window.location.origin.includes(domain.replace(/^https?:\/\//, ''))) {
          return false;
        }

        return true;
      }) ?? null
    );
  }

  /** @param {Record<string, string>} local */
  #renderMessage(local) {
    const messageEl = this.querySelector('[data-recommend-message]');
    if (!messageEl) return;

    messageEl.innerHTML = (messageEl.innerHTML ?? '')
      .replace('[country]', local.country ?? '')
      .replace('[store]', local.domain ?? '');
  }

  /** @param {Record<string, string>} local */
  #bindActions(local) {
    const yesBtn = /** @type {HTMLAnchorElement | null} */ (this.querySelector('[data-recommend-yes]'));
    const noBtn = this.querySelector('[data-recommend-no]');

    if (yesBtn) {
      const domain = (local.domain ?? '').replace(/\/$/, '');
      const path = (local.path ?? '/').replace(/^\/?/, '/');
      yesBtn.href = `${domain}${path}`;
      this.#setYesContent(yesBtn, local);
    }

    noBtn?.addEventListener('click', () => {
      this.#close();
    });
  }

  /**
   * Populates the yes button's flag (if available) and country name label.
   *
   * @param {HTMLAnchorElement} btn
   * @param {Record<string, string>} local
   */
  #setYesContent(btn, local) {
    const flagUrl = local.flag ?? null;
    const countryName = local.country ?? '';

    const flagWrapper = btn.querySelector('[data-recommend-yes-flag]');
    const img = flagWrapper?.querySelector('img');

    if (flagWrapper && img && flagUrl) {
      img.src = flagUrl;
      img.alt = countryName;
      flagWrapper.removeAttribute('hidden');
    }

    const label = btn.querySelector('[data-recommend-yes-label]');
    if (label) label.textContent = countryName;
  }

  /** @param {MouseEvent} e */
  #handleDialogClick = (e) => {
    const target = /** @type {Element} */ (e.target);

    if (target === this.#dialog || target.closest('[data-close-dialog]')) {
      this.#close();
    }
  };

  /** @param {Event} e */
  #handleCancel = (e) => {
    e.preventDefault();
    this.#close();
  };

  #open() {
    if (!this.#dialog || this.#dialog.open || this.#isClosing) return;
    this.#dialog.showModal();
    // Mark as seen on display so it shows at most once per session (per store).
    this.#markSeenThisSession();
  }

  async #close() {
    if (!this.#dialog || !this.#dialog.open || this.#isClosing) return;

    this.#isClosing = true;
    this.#dialog.classList.add('dialog-closing');

    await new Promise((resolve) => {
      this.#dialog?.addEventListener('animationend', resolve, { once: true });
    });

    this.#dialog.classList.remove('dialog-closing');
    this.#isClosing = false;
    this.#dialog.close();
  }

  #hasSeenThisSession() {
    try {
      return sessionStorage.getItem(SESSION_KEY) === '1';
    } catch {
      return false;
    }
  }

  #markSeenThisSession() {
    try {
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch {
      // sessionStorage unavailable (e.g. private mode) — fail open; the popup may reappear.
    }
  }

  #parseLocals() {
    try {
      const script = this.querySelector('script[data-locals]');
      return JSON.parse(script?.textContent ?? '[]');
    } catch {
      return [];
    }
  }
}

customElements.define('country-recommend-modal', CountryRecommendModal);
